import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import TextCard from './TextCard';

const FavoriteContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const FavoriteHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
`;

const FavoriteTitle = styled.h1`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const FavoriteStats = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-5px);
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#667eea' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.9)'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#5a6fd8' : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const FavoriteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(255, 255, 255, 0.8);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyActions = styled.div`
  margin-top: 2rem;
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(45deg, #FF6B6B, #4ECDC4);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: bold;
  transition: all 0.3s ease;
  margin: 0 0.5rem;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const BatchActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const BatchButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FavoritePage = ({ favorites, categories, onToggleFavorite }) => {
  const [selectedCategory, setSelectedCategory] = React.useState('all');
  const [sortBy, setSortBy] = React.useState('newest');

  const filteredFavorites = selectedCategory === 'all'
    ? favorites
    : favorites.filter(fav => fav.category === selectedCategory);

  const getSortedFavorites = () => {
    let sorted = [...filteredFavorites];

    switch (sortBy) {
      case 'oldest':
        return sorted.sort((a, b) => a.id - b.id);
      case 'category':
        return sorted.sort((a, b) => {
          const catA = categories.find(c => c.id === a.category);
          const catB = categories.find(c => c.id === b.category);
          return (catA?.name || '').localeCompare(catB?.name || '');
        });
      case 'length':
        return sorted.sort((a, b) => a.text.length - b.text.length);
      default: // newest
        return sorted.sort((a, b) => b.id - a.id);
    }
  };

  const sortedFavorites = getSortedFavorites();

  const handleClearAll = () => {
    if (window.confirm('确定要清空所有收藏吗？此操作不可撤销。')) {
      favorites.forEach(fav => {
        onToggleFavorite(fav.text, fav.category);
      });
    }
  };

  const handleExportFavorites = () => {
    const data = favorites.map(fav => {
      const category = categories.find(c => c.id === fav.category);
      return `${category?.name || '未知'}: ${fav.text}`;
    }).join('\n\n');

    const blob = new Blob([data], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '我的拍一拍收藏.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // 获取每个分类的收藏数量
  const getCategoryCounts = () => {
    const counts = {};
    favorites.forEach(fav => {
      counts[fav.category] = (counts[fav.category] || 0) + 1;
    });
    return counts;
  };

  const categoryCounts = getCategoryCounts();

  return (
    <FavoriteContainer className="fade-in">
      <BackButton to="/">
        ← 返回首页
      </BackButton>

      <FavoriteHeader>
        <FavoriteTitle>
          ❤️ 我的收藏
        </FavoriteTitle>
        <FavoriteStats>
          已收藏 {favorites.length} 条有趣的拍一拍文字
        </FavoriteStats>
      </FavoriteHeader>

      {favorites.length > 0 && (
        <>
          <BatchActions>
            <BatchButton onClick={handleExportFavorites}>
              📤 导出收藏
            </BatchButton>
            <BatchButton onClick={handleClearAll}>
              🗑️ 清空收藏
            </BatchButton>
          </BatchActions>

          <FilterBar>
            <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold' }}>
              分类筛选：
            </span>
            <FilterButton
              active={selectedCategory === 'all'}
              onClick={() => setSelectedCategory('all')}
            >
              全部 ({favorites.length})
            </FilterButton>
            {categories.map(category => {
              const count = categoryCounts[category.id] || 0;
              if (count === 0) return null;

              return (
                <FilterButton
                  key={category.id}
                  active={selectedCategory === category.id}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.icon} {category.name} ({count})
                </FilterButton>
              );
            })}
          </FilterBar>

          <FilterBar>
            <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold' }}>
              排序方式：
            </span>
            <FilterButton
              active={sortBy === 'newest'}
              onClick={() => setSortBy('newest')}
            >
              最新收藏
            </FilterButton>
            <FilterButton
              active={sortBy === 'oldest'}
              onClick={() => setSortBy('oldest')}
            >
              最早收藏
            </FilterButton>
            <FilterButton
              active={sortBy === 'category'}
              onClick={() => setSortBy('category')}
            >
              按分类
            </FilterButton>
            <FilterButton
              active={sortBy === 'length'}
              onClick={() => setSortBy('length')}
            >
              按长度
            </FilterButton>
          </FilterBar>

          <FavoriteGrid className="grid-2">
            {sortedFavorites.map((favorite) => {
              const category = categories.find(cat => cat.id === favorite.category);
              return (
                <TextCard
                  key={favorite.id}
                  text={favorite.text}
                  category={category}
                  onToggleFavorite={() => onToggleFavorite(favorite.text, favorite.category)}
                  isFavorite={true}
                />
              );
            })}
          </FavoriteGrid>
        </>
      )}

      {favorites.length === 0 && (
        <EmptyState>
          <EmptyIcon>💔</EmptyIcon>
          <h3>还没有收藏任何文字</h3>
          <p>去发现一些有趣的拍一拍文字，添加到你的收藏夹吧！</p>

          <EmptyActions>
            <ActionButton to="/">
              🏠 回到首页
            </ActionButton>
            <ActionButton to="/category/funny">
              😂 浏览搞笑
            </ActionButton>
          </EmptyActions>
        </EmptyState>
      )}
    </FavoriteContainer>
  );
};

export default FavoritePage;
